/**
 * 消息列表组件 (AI Elements 集成)
 *
 * 使用 AI Elements 组件渲染聊天消息：
 * - Conversation/ConversationMessages: 消息容器
 * - Message/MessageContent: 单条消息包装器
 * - Reasoning: 可折叠的思考过程显示
 * - Tool: 工具调用显示（可折叠详情）
 * - Streamdown: Markdown 内容渲染
 *
 * 功能：
 * - 历史消息显示（用户和 AI 响应）
 * - 实时流式输出
 * - 思考过程折叠/展开
 * - 工具调用显示
 * - 自动滚动到底部
 * - 技能激活卡片
 * - 子智能体调用卡片
 * - 消息操作（复制、删除、重新生成）
 *
 * 注意：所有用户界面文字、提示信息、状态文字必须使用中文
 *
 * @module MessageList
 */

import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import type { ChatMessage, StreamingMessageState } from '../../types/chat';
import {
  Conversation,
  ConversationMessages,
} from '../ai-elements/conversation';
import {
  Message,
  MessageContent,
  MessageActions,
  MessageAction,
} from '../ai-elements/message';
import {
  Reasoning,
} from '../ai-elements/reasoning';
import {
  ToolCardRenderer,
} from '../ai-elements/tool-registry';
import {
  toAIElementsMessages,
  streamingPartsToAIElements,
  type AIElementsMessageProps,
  type ToolCallData,
} from '../../adapters/chatAdapter';
import { parseReferences } from './ReferenceTooltip';
import { SkillActivationCard } from './SkillActivationCard';
import { SubagentInvocationCard } from './SubagentInvocationCard';
import { useAgentStore } from '../../store/agentStore';

// ==================== Component Interfaces ====================

/**
 * MessageList component props interface
 */
interface MessageListProps {
  messages: ChatMessage[];
  sessionId?: string;
  streamingMessage: StreamingMessageState | null;
  isStreaming: boolean;
  loading?: boolean;
  sessionTitle?: string;
  onRegenerateMessage?: (messageIndex: number) => void;
}

// ==================== Empty State Component ====================

/**
 * Empty state component
 *
 * Displays prompt when there are no messages
 */
export const EmptyState: React.FC = React.memo(() => {
  return (
    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
      <svg
        className="w-16 h-16 mb-4 text-muted-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
      <p className="text-center text-sm">
        开始与AI智能体对话
        <br />
        支持设定编写、章节创建、内容编辑
      </p>
    </div>
  );
});

EmptyState.displayName = 'EmptyState';

// ==================== Loading Indicator Component ====================

/**
 * Loading indicator component props interface
 */
interface LoadingIndicatorProps {
  /** Loading text */
  text?: string;
}

/**
 * Spinner icon component
 */
const SpinnerIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg
    className={`animate-spin ${className}`}
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

/**
 * Loading indicator component
 *
 * Displays processing status
 */
export const LoadingIndicator: React.FC<LoadingIndicatorProps> = React.memo(({ text = '处理中...' }) => {
  return (
    <div className="flex justify-center py-4">
      <div className="flex items-center gap-2 text-muted-foreground" role="status" aria-live="polite">
        <SpinnerIcon className="w-5 h-5" />
        <span className="text-sm">{text}</span>
      </div>
    </div>
  );
});

LoadingIndicator.displayName = 'LoadingIndicator';

// ==================== Icons for Message Actions ====================

const CopyIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const RefreshIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const TrashIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

// ==================== Message Action Buttons Component ====================

interface MessageActionButtonsProps {
  content: string;
  role: 'user' | 'assistant';
  messageIndex: number;
  onRegenerate?: (messageIndex: number) => void;
}

const MessageActionButtons: React.FC<MessageActionButtonsProps> = ({
  content,
  role,
  messageIndex,
  onRegenerate,
}) => {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }, [content]);

  const handleRegenerate = useCallback(() => {
    if (onRegenerate) {
      onRegenerate(messageIndex);
    }
  }, [onRegenerate, messageIndex]);

  return (
    <MessageActions>
      <MessageAction onClick={handleCopy} title={copied ? '已复制' : '复制'}>
        {copied ? <CheckIcon className="w-3.5 h-3.5 text-success" /> : <CopyIcon className="w-3.5 h-3.5" />}
      </MessageAction>
      {role === 'assistant' && onRegenerate && (
        <MessageAction onClick={handleRegenerate} title="重新生成">
          <RefreshIcon className="w-3.5 h-3.5" />
        </MessageAction>
      )}
    </MessageActions>
  );
};

// ==================== System Message Component ====================

interface SystemMessageProps {
  content: string;
}

const SystemMessage: React.FC<SystemMessageProps> = ({ content }) => {
  return (
    <div className="flex justify-center py-2" role="status" aria-label={`系统消息: ${content}`}>
      <span className="text-xs text-muted-foreground bg-muted/50 px-3 py-1 rounded-full">
        {content}
      </span>
    </div>
  );
};

// ==================== Main MessageList Component ====================

/**
 * Message list main component
 *
 * Provides complete message list functionality:
 * - Historical message display
 * - Real-time streaming output
 * - Thinking process folding/expanding
 * - Tool call display
 * - Auto-scroll
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  sessionId,
  streamingMessage,
  isStreaming,
  loading = false,
  sessionTitle,
  onRegenerateMessage: _onRegenerateMessage,
}) => {
  const currentAgent = useAgentStore((state) => state.getCurrentAgent());
  const currentAgentName = currentAgent?.name;

  console.log(
    '[MessageList] Render - messages count:',
    messages.length,
    'sessionId:',
    sessionId,
    'currentAgentName:',
    currentAgentName,
    'streamingMessage parts:',
    streamingMessage?.parts?.length,
    'isStreaming:',
    isStreaming
  );

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const conversationMessagesRef = useRef<HTMLDivElement>(null);

  const isUserAtBottomRef = useRef(true);
  const prevMessagesLengthRef = useRef(messages.length);

  const SCROLL_THRESHOLD = 50;

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const atBottom = target.scrollHeight - target.scrollTop - target.clientHeight < SCROLL_THRESHOLD;
    isUserAtBottomRef.current = atBottom;
  }, []);

  useEffect(() => {
    const isNewMessage = messages.length > prevMessagesLengthRef.current;

    if (isUserAtBottomRef.current || isNewMessage) {
      messagesEndRef.current?.scrollIntoView({
        behavior: isStreaming ? 'instant' : 'smooth',
      });
    }

    prevMessagesLengthRef.current = messages.length;
  }, [messages, streamingMessage, isStreaming]);

  const subagentSessionsMap = useAgentStore((state) => state.subagentSessions);

  const aiElementsMessages = useMemo(() => {
    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const converted = toAIElementsMessages(messages, sessionId, currentAgentName);

    if (converted.length > 0 && converted[0].type !== 'system') {
      const sessionStartMsg: AIElementsMessageProps = {
        id: 'session-start',
        type: 'system',
        role: 'system',
        content: sessionTitle ? `会话: ${sessionTitle}` : '新对话已开始',
        createdAt: converted[0].createdAt || Date.now(),
        hasTime: false,
      };
      converted.unshift(sessionStartMsg);
    }

    let lastTimestamp = 0;
    for (let i = 0; i < converted.length; i++) {
      const msg = converted[i];

      if (msg.type === 'system') {
        msg.hasTime = false;
        continue;
      }

      if (lastTimestamp === 0) {
        msg.hasTime = true;
        lastTimestamp = msg.createdAt || Date.now();
        continue;
      }

      const currentTimestamp = msg.createdAt || Date.now();
      const timeDiff = currentTimestamp - lastTimestamp;

      msg.hasTime = timeDiff >= FIVE_MINUTES_MS;

      lastTimestamp = currentTimestamp;
    }

    if (isStreaming && streamingMessage && streamingMessage.parts.length > 0) {
      const streamingMessages = streamingPartsToAIElements({
        parts: streamingMessage.parts,
        isComplete: streamingMessage.isComplete,
        sessionId,
        streamingStartTime: Date.now(),
      });

      if (streamingMessages.length > 0) {
        for (const streamingMsg of streamingMessages) {
          const lastMessage = converted[converted.length - 1];
          if (lastMessage && lastMessage.type !== 'system') {
            const lastMsgTimestamp = lastMessage.createdAt || 0;
            const streamingTimestamp = streamingMsg.createdAt || Date.now();
            const timeDiff = streamingTimestamp - lastMsgTimestamp;

            streamingMsg.hasTime = timeDiff >= FIVE_MINUTES_MS;
          } else {
            streamingMsg.hasTime = true;
          }

          converted.push(streamingMsg);
        }
      }
    }

    // Merge subagent session real-time data into corresponding messages
    converted.forEach((msg) => {
      if (msg.type === 'subagent-invocation' && msg.subagentInvocation) {
        // Get real-time data from agentStore
        const subagentName = msg.subagentInvocation.subagentName;
        const session = Array.from(subagentSessionsMap.values()).find(
          (s) => s.agentName === subagentName && (s.parentSessionId === sessionId || !sessionId)
        );

        if (session) {
          // Status mapping: agentStore status -> subagentInvocation status
          const statusMap: Record<string, 'invoking' | 'working' | 'completed' | 'error'> = {
            pending: 'invoking',
            running: 'working',
            completed: 'completed',
            failed: 'error',
            timeout: 'error',
          };

          // Merge real-time data
          msg.subagentInvocation = {
            ...msg.subagentInvocation,
            status: statusMap[session.status] || (session.status as 'invoking' | 'working' | 'completed' | 'error'),
            streamContent: session.streamContent,
            toolCalls: session.toolCalls?.map((tc) => ({
              tool_call_id: tc.tool_call_id,
              tool_name: tc.tool_name,
              status: tc.status,
              tool_args: tc.tool_args,
              result: tc.result,
              error: tc.error,
              timestamp: tc.timestamp,
            })),
            messages: session.messages,
            result: session.result,
            error: session.error,
            executionTime: session.duration,
          };
        }
      }
    });

    return converted;
  }, [
    messages,
    sessionId,
    isStreaming,
    streamingMessage,
    sessionTitle,
    currentAgentName,
    subagentSessionsMap,
  ]);

  /**
   * Render message content
   * Uses AI Elements component pattern
   */
  const renderMessage = useCallback(
    (msg: AIElementsMessageProps, index: number) => {
      const { type, role, content, id } = msg;

      // Extract message index (for regenerate)
      const messageIndex = typeof id === 'string' ? parseInt(id.split('-')[1] || '0', 10) : index;

      // Skill activation message
      if (type === 'skill-activation' && msg.skillActivation) {
        return (
          <div
            key={id}
            className="my-2 px-4"
            role="region"
            aria-label={`技能激活: ${msg.skillActivation.skillName}`}
          >
            <SkillActivationCard data={msg.skillActivation} />
            {content && (
              <Message role="assistant">
                <MessageContent>
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">{content}</div>
                </MessageContent>
              </Message>
            )}
          </div>
        );
      }

      // Subagent invocation message
      if (type === 'subagent-invocation' && msg.subagentInvocation) {
        const subagentName = msg.subagentInvocation.subagentName;

        const handleClose = () => {
          // Find and remove corresponding session by subagentName
          const sessions = useAgentStore.getState().subagentSessions;
          const session = Array.from(sessions.values()).find((s) => s.agentName === subagentName);
          if (session) {
            useAgentStore.getState().removeSubagentSession(session.sessionId);
          }
        };

        return (
          <div
            key={id}
            className="my-2 px-4"
            role="region"
            aria-label={`子智能体调用: ${msg.subagentInvocation.subagentName}`}
          >
            {/* Display thinking process if present */}
            {msg.reasoning && (
              <Message role="assistant" className="mb-2">
                <MessageContent>
                  <Reasoning
                    isStreaming={isStreaming && !msg.reasoning.isDone}
                    reasoningTime={msg.reasoning.reasoningTime}
                    defaultOpen={isStreaming && !msg.reasoning.isDone}
                  >
                    <div className="whitespace-pre-wrap">{msg.reasoning.content || ''}</div>
                  </Reasoning>
                </MessageContent>
              </Message>
            )}
            <SubagentInvocationCard data={msg.subagentInvocation} onClose={handleClose} />
          </div>
        );
      }

      // Tool calls message
      if (type === 'tool-calls' && msg.toolCalls) {
        const filteredToolCalls = msg.toolCalls.filter((tc: ToolCallData) => tc.toolName !== 'ask_user');
        if (filteredToolCalls.length === 0) return null;
        return (
          <div key={id} className="space-y-2 px-4" role="region" aria-label="工具调用">
            {filteredToolCalls.map((toolCall: ToolCallData, idx: number) => (
              <ToolCardRenderer
                key={`${toolCall.toolName}-${idx}`}
                toolCall={toolCall}
              />
            ))}
          </div>
        );
      }

      // System message
      if (type === 'system') {
        return (
          <div key={id} className="px-4">
            <SystemMessage content={content || ''} />
          </div>
        );
      }

      // Text message (includes reasoning via parts)
      if (type === 'text') {
        const isUser = role === 'user';

        if (msg.parts && msg.parts.length > 0) {
          return (
            <div key={id} className="group" role="article" aria-label={isUser ? '你的消息' : 'AI助手消息'}>
              <Message
                role={role}
                parts={msg.parts}
                partConfig={{
                  isStreaming: id?.startsWith('streaming-') && isStreaming,
                  streamingStartTime: msg.createdAt,
                }}
                actions={
                  !id?.startsWith('streaming-') && role !== 'system' ? (
                    <MessageActionButtons
                      content={msg.parts
                        .filter(p => p.type === 'text')
                        .map(p => (p as { content: string }).content)
                        .join('')}
                      role={role}
                      messageIndex={messageIndex}
                      onRegenerate={_onRegenerateMessage}
                    />
                  ) : undefined
                }
              />
            </div>
          );
        }

        return (
          <div key={id} className="group" role="article" aria-label={isUser ? '你的消息' : 'AI助手消息'}>
            <Message
              role={role}
              actions={
                role !== 'system' ? (
                  <MessageActionButtons
                    content={content || ''}
                    role={role}
                    messageIndex={messageIndex}
                    onRegenerate={_onRegenerateMessage}
                  />
                ) : undefined
              }
            >
              <MessageContent>
                <div className="whitespace-pre-wrap text-sm leading-relaxed">{parseReferences(content || '')}</div>
              </MessageContent>
            </Message>
          </div>
        );
      }

      // Image message
      if (type === 'image' && msg.imageUrl) {
        return (
          <div key={id} className="px-4" role="img" aria-label={`${role === 'user' ? '你的' : 'AI助手'}消息: 图片`}>
            <Message role={role}>
              <MessageContent>
                <img
                  src={msg.imageUrl}
                  alt="Message image"
                  className="max-w-full rounded-lg"
                />
              </MessageContent>
            </Message>
          </div>
        );
      }

      // Default render
      return (
        <div key={id} className="px-4">
          <Message role={role}>
            <MessageContent>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{content || ''}</div>
            </MessageContent>
          </Message>
        </div>
      );
    },
    [_onRegenerateMessage, isStreaming]
  );

  // Empty state
  if (messages.length === 0 && !isStreaming) {
    return (
      <div
        className="flex-1 flex items-center justify-center"
        role="status"
        aria-label="无消息"
      >
        <EmptyState />
      </div>
    );
  }

  return (
    <div
      id="chat-messages"
      className="flex-1 overflow-hidden"
      role="log"
      aria-live="polite"
      aria-label="聊天消息列表"
      aria-relevant="additions"
    >
      {/* Use AI Elements Conversation component */}
      <Conversation className="h-full">
        <ConversationMessages ref={conversationMessagesRef} className="h-full" onScroll={handleScroll}>
          {aiElementsMessages.map((msg, index) => renderMessage(msg, index))}

          {/* Loading indicator (only displayed when loading is true) */}
          {loading && <LoadingIndicator />}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} aria-hidden="true" />
        </ConversationMessages>
      </Conversation>
    </div>
  );
};

export default MessageList;
